import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import nitiStoryImage from "../assets/mou/mou emrss.jpeg";
import "./NitiFeature.scss";
import AsOfNote from "./AsOfNote";
import { nitiFigures } from "../data/stats";

// The story CoGrad was featured in, on NITI Aayog's Frontier Tech Hub.
// The CTA must always point at the original — never at an internal mirror.
export const NITI_STORY_URL =
  "https://frontiertech.niti.gov.in/story/using-ai-this-digital-platform-reduces-classroom-prep-time-for-teachers/";

export const NITI_ARTICLE_PATH = "/blog/cograd-featured-by-niti-aayog";

// Figures below are reported in the NITI Aayog feature itself — nothing added.
const FACTS = [
  { figure: "10,000", label: "teachers using Medha AI" },
  { figure: "~2,00,000", label: "schools reached" },
  { figure: "87%", label: "students improved in 60 days" },
];

export default function NitiFeature() {
  return (
    <section className="nf-wrap" aria-labelledby="nf-heading">
      <div className="nf-inner">
        {/* ── Claim ──────────────────────────────────────────────────────── */}
        <motion.div
          className="nf__content"
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          <p className="nf__eyebrow">Featured by NITI Aayog</p>

          <h2 className="nf__h2" id="nf-heading">
            <span className="nf__h2-stat">30&ndash;40%</span> Less Prep Time.
            <br />
            More Time to Teach.
          </h2>

          <p className="nf__lede">
            CoGrad&rsquo;s Medha AI has been featured by NITI Aayog&rsquo;s Frontier
            Technologies platform for helping teachers reduce classroom
            preparation time.
          </p>
        </motion.div>

        {/* ── Figures, all attributed to the feature ────────────────────── */}
        <dl className="nf__stats">
          {FACTS.map((f) => (
            <div className="nf__stats-item" key={f.label}>
              <dt className="nf__stats-figure">{f.figure}</dt>
              <dd className="nf__stats-label">{f.label}</dd>
            </div>
          ))}
        </dl>
        {/* These are the article's figures, and its school count is far larger
            than the partner-school count we report elsewhere. Saying whose
            numbers they are is what keeps the two from reading as a
            contradiction. */}
        <AsOfNote text={nitiFigures.source} className="nf__asof" />

        {/* ── The feature itself, as an article clipping ──────────────────── */}
        <motion.a
          className="nf__card"
          href={NITI_STORY_URL}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.08 }}
        >
          <span className="nf__card-figure">
            <img
              src={nitiStoryImage}
              alt="CoGrad team at the Eklavya Model Residential Schools MoU signing — the photograph used on the NITI Aayog Frontier Tech Hub feature"
              loading="lazy"
              width="1600"
              height="1067"
            />
          </span>

          <span className="nf__card-body">
            <span className="nf__card-source">
              <b>NITI Aayog</b>
              <i>Frontier Technologies</i>
            </span>

            <span className="nf__card-rule" aria-hidden="true" />

            <span className="nf__card-meta">
              <em>Frontier Voices</em>
              <span className="nf__card-dot" aria-hidden="true" />
              {/* "Updated" is dropped on narrow screens so the meta line never wraps. */}
              <span className="nf__card-meta-word">Updated </span>31 August 2026
            </span>

            <span className="nf__card-headline">
              Using AI, This Digital Platform Reduces Classroom Prep Time by
              30&ndash;40% for Teachers
            </span>

            <span className="nf__card-foot">
              <span className="nf__card-read">
                Read Story
                <ArrowUpRight size={15} strokeWidth={2.4} />
              </span>
              <span className="nf__card-host">frontiertech.niti.gov.in</span>
            </span>
          </span>
        </motion.a>

        {/* ── Actions (below the copy on desktop, below the card on mobile) ─ */}
        <div className="nf__actions">
          <a
            className="nf__cta"
            href={NITI_STORY_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Read the NITI Aayog Story
            <ArrowUpRight size={17} strokeWidth={2.4} />
          </a>
          <a className="nf__link" href={NITI_ARTICLE_PATH}>
            {/* Trailing half is dropped on narrow screens to keep this one line. */}
            What the feature says<span className="nf__link-tail"> about Medha AI</span>
          </a>
        </div>
      </div>

      {/* "Featured by" — deliberately not framed as a partnership. */}
      <p className="nf__note">
        Figures as reported in the NITI Aayog Frontier Tech Hub feature, an
        independent editorial story. CoGrad is not a NITI Aayog partner.
      </p>
    </section>
  );
}
